import React from 'react';
import { Helmet as MetaTags } from 'react-helmet';
import type { RouteComponentProps } from '@gatsbyjs/reach-router';
import ContentContainerDefault from '~/components/molecule/ContentContainer';
import createPageTitle from '~/util/createPageTitle';
import OverviewListHeader from '~/components/molecule/OverviewListHeader';
import AnimatedBlock from '~/components/atom/AnimatedBlock';
import {
  Lfa_Account_Status,
  useGetLfaAccountSettingsQuery,
  useGetLfaConversionDataQuery,
} from '~/graphql/types';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import hasAnyServicesV2Selected from './servicesV2/utils/hasAnyServicesSelected';
import UpdateSettingsForm from './components/UpdateSettingsForm';
import Loading from '~/components/atom/Loading';
import AppErrorScreen from '~/components/template/AppErrorScreen';
import styled from 'styled-components';
import CopyTextBlock from '~/components/molecule/CopyTextBlock';
import StatusUpdate from './components/StatusUpdate';
import JustificationContainer from '~/components/atom/JustificationContainer';
import StatusDisplay from './components/StatusDisplay';
import { Heading4 } from '~/components/atom/Typography';
import Validation from '~/util/Validation';
import ValidationMessages from './components/ValidationMessages';

export const MAX_LFA_ACCOUNT_SETTINGS_CONTAINER_WIDTH = '1100px';

const text = {
  pageTitle: 'LeadAssist Instellingen',
  noAccountSettingsFound: 'Geen instellingen gevonden.',
  waitingOnPhoneNumber: 'Vul een telefoonnummer in om LeadAssist te activeren',
  flynckSettingsNotSet: 'Flynck instellingen zijn niet ingesteld.',
  phoneNumberNotValid: 'Callcenter telefoonnummer is niet geldig.',
  noServicesSelected: 'Geen actieve diensten geselecteerd.',
  accountDetails: 'Account Details',
  accountStatus: 'Account status',
};

const LFASettings: React.FCC<RouteComponentProps> = () => {
  const { id: accountId } = useCurrentAccount();
  const {
    data: accountSettingsData,
    loading: accountSettingsLoading,
    error: accountSettingsError,
    refetch: refetchAccountSettings,
  } = useGetLfaAccountSettingsQuery({
    variables: { accountId },
    fetchPolicy: 'no-cache',
  });

  const {
    data: conversionData,
    loading: conversionDataLoading,
    error: conversionDataError,
    refetch: refetchConversionData,
  } = useGetLfaConversionDataQuery({
    variables: {
      accountId,
    },
  });

  if (accountSettingsLoading || conversionDataLoading) {
    return (
      <ContentContainerDefault>
        <MetaTags>
          <title>{createPageTitle(text.pageTitle)}</title>
        </MetaTags>
        <Loading />
      </ContentContainerDefault>
    );
  }

  if (
    !accountSettingsData ||
    !accountSettingsData.getLfaAccountSettings ||
    !conversionData ||
    accountSettingsError ||
    conversionDataError
  ) {
    return (
      <ContentContainerDefault>
        <MetaTags>
          <title>{createPageTitle(text.pageTitle)}</title>
        </MetaTags>
        <OverviewListHeader title={text.pageTitle} />
        <AppErrorScreen message={text.noAccountSettingsFound} />
      </ContentContainerDefault>
    );
  }

  const refetch = () =>
    Promise.all([refetchAccountSettings(), refetchConversionData()]);

  const { status, lfaAccountToken, services, servicesV2 } =
    accountSettingsData.getLfaAccountSettings;

  const isFlynckSetup =
    accountSettingsData.getLfaAccountSettings.flynck?.contextID != null &&
    accountSettingsData.getLfaAccountSettings.flynck?.importSetupID != null;
  const isPhoneNumberValid = Validation.Phone.isValidDutchPhoneNumber(
    accountSettingsData.getLfaAccountSettings.callbackPhoneNumber,
  );
  const atLeastOneServiceSelected =
    servicesV2 != null
      ? hasAnyServicesV2Selected(servicesV2)
      : services.length > 0;

  const validationMessages: Array<string> = (() => {
    const messages: Array<string> = [];
    if (!isFlynckSetup) {
      messages.push(text.flynckSettingsNotSet);
    }
    if (!isPhoneNumberValid) {
      messages.push(text.phoneNumberNotValid);
    }
    if (!atLeastOneServiceSelected) {
      messages.push(text.noServicesSelected);
    }
    return messages;
  })();

  const readyToActivate =
    isFlynckSetup &&
    isPhoneNumberValid &&
    atLeastOneServiceSelected &&
    status === Lfa_Account_Status.Activating;

  return (
    <ContentContainerDefault
      maxContentWidth={MAX_LFA_ACCOUNT_SETTINGS_CONTAINER_WIDTH}
    >
      <MetaTags>
        <title>{createPageTitle(text.pageTitle)}</title>
      </MetaTags>

      <OverviewListHeader title={text.pageTitle} />

      <JustificationContainer
        gap="base"
        width="100%"
        justification="stretch"
        align="stretch"
      >
        <TopAnimatedBlock manualLayout>
          <Heading4 size="m">{text.accountDetails}</Heading4>
          <CopyTextBlock
            label="Account token"
            textToCopy={lfaAccountToken}
            width="max-content"
          />
        </TopAnimatedBlock>

        <TopAnimatedBlock manualLayout>
          <JustificationContainer width="100%" direction="column" gap="xs">
            <Heading4 size="m" margin={[null]}>
              {text.accountStatus}
            </Heading4>
            <JustificationContainer
              align="center"
              justification="space-between"
              gap="s"
              width="100%"
            >
              <StatusDisplay
                status={status}
                readyToActivate={readyToActivate}
              />
              <StatusUpdate
                status={status}
                readyToActivate={readyToActivate}
                refetch={refetch}
                callbackPhoneNumber={
                  accountSettingsData.getLfaAccountSettings
                    .callbackPhoneNumber || null
                }
              />
            </JustificationContainer>

            <ValidationMessages messages={validationMessages} />
          </JustificationContainer>
        </TopAnimatedBlock>
      </JustificationContainer>

      <StyledAnimatedBlock manualLayout>
        <UpdateSettingsForm
          queryData={accountSettingsData.getLfaAccountSettings}
          allServices={conversionData.getLfaConversionData.allServices}
          refetch={refetchAccountSettings}
        />
      </StyledAnimatedBlock>
    </ContentContainerDefault>
  );
};

const TopAnimatedBlock = styled(AnimatedBlock)`
  flex: 1 0 45%;
  width: 100%;
  margin-bottom: ${({ theme }) => theme.space('l')};
`;

const StyledAnimatedBlock = styled(AnimatedBlock)`
  width: 100%;
  margin-bottom: ${({ theme }) => theme.space('l')};
`;

export default LFASettings;
